import React, {useState} from 'react';
import {ActivityIndicator, Pressable, View} from 'react-native';
import {makeStyles} from '@rneui/themed';

import Text from '../../themed/Text';

// theme.size and theme.colors.legacy/linkPrimary are custom tokens this
// codebase's rewardsTheme adds via createTheme's object literal, but the
// @rneui/themed Theme/Colors types are not augmented for them (see
// app/theme/index.d.ts — commented out), so they don't type-check anywhere
// in the codebase (incl. MemberCard/index.tsx, rewardsTheme.ts itself).
// theme.colors.grey3/error/white ARE real @rneui Colors tokens, kept as-is.
const useStyles = makeStyles(theme => ({
  container: {
    marginTop: 8,
  },
  field: {
    marginBottom: 16,
  },
  label: {
    color: theme.colors?.black,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: theme.colors?.grey3 ?? '#A4A4A4',
    borderRadius: 8,
    backgroundColor: theme.colors?.white,
  },
  triggerValue: {
    color: theme.colors?.black,
  },
  triggerPlaceholder: {
    color: theme.colors?.grey3 ?? '#A4A4A4',
  },
  optionList: {
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: theme.colors?.grey3 ?? '#A4A4A4',
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  option: {
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  optionText: {
    color: theme.colors?.black,
  },
  loadingContainer: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  errorContainer: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  errorText: {
    color: theme.colors?.error ?? '#D8402E',
    marginBottom: 12,
    textAlign: 'center',
  },
  retryText: {
    color: '#1A56DB',
    fontWeight: '600',
  },
}));

type DropdownProps = {
  testID: string;
  label: string;
  placeholder: string;
  options: string[];
  selected: string | null;
  onSelect: (value: string) => void;
};

function Dropdown({testID, label, placeholder, options, selected, onSelect}: DropdownProps) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);

  return (
    <View style={styles.field} testID={testID}>
      <Text style={styles.label}>{label}</Text>
      <Pressable
        style={styles.trigger}
        testID={`${testID}-trigger`}
        onPress={() => setOpen(value => !value)}>
        <Text style={selected ? styles.triggerValue : styles.triggerPlaceholder}>
          {selected ?? placeholder}
        </Text>
      </Pressable>
      {open ? (
        <View style={styles.optionList} testID={`${testID}-options`}>
          {options.map((option, index) => (
            <Pressable
              key={option}
              style={styles.option}
              testID={`${testID}-option-${index}`}
              onPress={() => {
                onSelect(option);
                setOpen(false);
              }}>
              <Text style={styles.optionText}>{option}</Text>
            </Pressable>
          ))}
        </View>
      ) : null}
    </View>
  );
}

type LmgDepartmentStatePickerProps = {
  departments: string[];
  states: string[];
  selectedDepartment: string | null;
  selectedState: string | null;
  onSelectDepartment: (value: string) => void;
  onSelectState: (value: string) => void;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  testID?: string;
};

export default function LmgDepartmentStatePicker({
  departments,
  states,
  selectedDepartment,
  selectedState,
  onSelectDepartment,
  onSelectState,
  loading = false,
  error = false,
  onRetry,
  testID = 'lmg-department-state-picker',
}: LmgDepartmentStatePickerProps) {
  const styles = useStyles();

  if (loading) {
    return (
      <View style={styles.loadingContainer} testID={`${testID}-loading`}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer} testID={`${testID}-error`}>
        <Text style={styles.errorText}>
          We couldn&apos;t load the departments. Please try again.
        </Text>
        <Pressable testID={`${testID}-retry`} onPress={() => onRetry?.()}>
          <Text style={styles.retryText}>Retry</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={styles.container} testID={testID}>
      <Dropdown
        testID={`${testID}-department`}
        label="Department"
        placeholder="Select department"
        options={departments}
        selected={selectedDepartment}
        onSelect={onSelectDepartment}
      />
      <Dropdown
        testID={`${testID}-state`}
        label="State"
        placeholder="Select state"
        options={states}
        selected={selectedState}
        onSelect={onSelectState}
      />
    </View>
  );
}
